import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  UserPlusIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  TrashIcon,
  XMarkIcon,
  CalendarDaysIcon,
  PauseCircleIcon,
  NoSymbolIcon,
  SparklesIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  UsersIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ClockIcon,
  BuildingOffice2Icon,
} from "@heroicons/react/24/outline";
import {
  apiService,
  PlatformInvite,
  PlatformSubscriptionDisplayStatus,
  PlatformUsersSummary,
  PlatformUserRow,
} from "../services/apiService";

interface PlatformUsersPanelProps {
  onClose?: () => void;
  variant?: "inline" | "modal";
}

const STATUS_LABELS: Record<PlatformSubscriptionDisplayStatus, string> = {
  active: "Activo",
  paused: "Pausado",
  cancelled: "Cancelado",
  scheduled: "Programado",
  expired: "Expirado",
};

const STATUS_STYLES: Record<PlatformSubscriptionDisplayStatus, string> = {
  active: "bg-emerald-500/15 text-emerald-700 ring-emerald-500/25",
  paused: "bg-amber-500/15 text-amber-800 ring-amber-500/25",
  cancelled: "bg-rose-500/15 text-rose-700 ring-rose-500/25",
  scheduled: "bg-sky-500/15 text-sky-800 ring-sky-500/25",
  expired: "bg-zinc-500/15 text-zinc-600 ring-zinc-500/25",
};

function resolveDisplayStatus(u: PlatformUserRow): PlatformSubscriptionDisplayStatus {
  return u.subscriptionDisplayStatus || u.subscriptionStatus || "active";
}

function formatDateInput(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function formatDateDisplay(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function formatDateTimeDisplay(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTokens(n: number) {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function getInitials(u: PlatformUserRow) {
  const fromName = (u.name || u.companyName || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
  if (fromName.length >= 2) return fromName;
  const email = u.email || "";
  return (email[0] || "?").toUpperCase() + (email[1] || "").toUpperCase();
}

function getPasswordStrength(password: string) {
  const checks = {
    length: password.length >= 10,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_\-+=[\]{};:'",.<>/?\\|`~]/.test(password),
  };
  const score = Object.values(checks).filter(Boolean).length;
  let strength = "Muy débil";
  let color = "text-rose-600";
  if (score >= 5) {
    strength = "Muy fuerte";
    color = "text-emerald-700";
  } else if (score >= 4) {
    strength = "Fuerte";
    color = "text-emerald-600";
  } else if (score >= 3) {
    strength = "Buena";
    color = "text-amber-600";
  } else if (score >= 2) {
    strength = "Débil";
    color = "text-orange-600";
  }
  return { strength, color, score, checks };
}

function validatePlatformPassword(password: string): string | null {
  if (!password || password.length < 10) {
    return "La contraseña debe tener al menos 10 caracteres";
  }
  if (!/[a-z]/.test(password)) return "Debe incluir una letra minúscula";
  if (!/[A-Z]/.test(password)) return "Debe incluir una letra mayúscula";
  if (!/[0-9]/.test(password)) return "Debe incluir un número";
  if (!/[!@#$%^&*()_\-+=[\]{};:'",.<>/?\\|`~]/.test(password)) {
    return "Debe incluir un símbolo";
  }
  return null;
}

export const PlatformUsersPanel: React.FC<PlatformUsersPanelProps> = ({
  onClose,
  variant = "inline",
}) => {
  const [users, setUsers] = useState<PlatformUserRow[]>([]);
  const [summary, setSummary] = useState<PlatformUsersSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", companyName: "" });
  const [lastInvite, setLastInvite] = useState<PlatformInvite | null>(null);
  const [busy, setBusy] = useState(false);
  const [dateModalUser, setDateModalUser] = useState<PlatformUserRow | null>(null);
  const [dateForm, setDateForm] = useState({ accessStartsAt: "", accessEndsAt: "" });
  const [passwordModalUser, setPasswordModalUser] = useState<PlatformUserRow | null>(null);
  const [passwordForm, setPasswordForm] = useState({ password: "", confirmPassword: "" });
  const [showPasswordFields, setShowPasswordFields] = useState({ password: false, confirm: false });
  const [passwordModalError, setPasswordModalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const statusCounts = useMemo(() => {
    const counts = { active: 0, paused: 0, cancelled: 0, scheduled: 0, expired: 0 };
    users.forEach((u) => {
      const key = resolveDisplayStatus(u);
      if (key in counts) counts[key as keyof typeof counts] += 1;
    });
    return counts;
  }, [users]);

  const maxAiTokens30d = useMemo(
    () => Math.max(1, ...users.map((u) => u.ai?.tokensTotal30d || 0)),
    [users]
  );

  const aiTokensMonth = summary?.aiTokensMonth ?? users.reduce((s, u) => s + (u.ai?.tokensTotal30d || 0), 0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await apiService.getPlatformUsers();
    if (res.error) {
      setError(res.message || res.error);
      setUsers([]);
      setSummary(null);
    } else {
      const data = res.data as { users?: PlatformUserRow[]; summary?: PlatformUsersSummary };
      setUsers(data?.users || []);
      setSummary(data?.summary || null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!successMessage) return;
    const t = window.setTimeout(() => setSuccessMessage(null), 5000);
    return () => window.clearTimeout(t);
  }, [successMessage]);

  const copyInvite = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      prompt("Copia este enlace de invitación:", url);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await apiService.createPlatformUser({
      email: form.email.trim(),
      name: form.name.trim(),
      companyName: form.companyName.trim() || form.name.trim(),
    });
    setBusy(false);
    if (res.error) {
      setError(res.message || res.error);
      return;
    }
    const data = res.data as { invite?: PlatformInvite };
    if (data?.invite) {
      setLastInvite(data.invite);
      await copyInvite(data.invite.url);
    }
    setShowForm(false);
    setForm({ email: "", name: "", companyName: "" });
    load();
  };

  const handleRegenerate = async (userId: string) => {
    setBusy(true);
    const res = await apiService.regeneratePlatformInvite(userId);
    setBusy(false);
    if (res.error) {
      setError(res.message || res.error);
      return;
    }
    const invite = (res.data as { invite?: PlatformInvite })?.invite;
    if (invite) {
      setLastInvite(invite);
      await copyInvite(invite.url);
    }
  };

  const handlePause = async (u: PlatformUserRow) => {
    if (!window.confirm(`¿Pausar acceso de ${u.email}?`)) return;
    setBusy(true);
    const res = await apiService.pausePlatformSubscription(u.id);
    setBusy(false);
    if (res.error) setError(res.message || res.error);
    else load();
  };

  const handleCancel = async (u: PlatformUserRow) => {
    if (!window.confirm(`¿Cancelar suscripción de ${u.email}? No podrá acceder al ERP.`)) return;
    setBusy(true);
    const res = await apiService.cancelPlatformSubscription(u.id);
    setBusy(false);
    if (res.error) setError(res.message || res.error);
    else load();
  };

  const openDateModal = (u: PlatformUserRow) => {
    setDateModalUser(u);
    setDateForm({
      accessStartsAt: formatDateInput(u.accessStartsAt),
      accessEndsAt: formatDateInput(u.accessEndsAt),
    });
  };

  const openPasswordModal = (u: PlatformUserRow) => {
    setPasswordModalUser(u);
    setPasswordForm({ password: "", confirmPassword: "" });
    setPasswordModalError(null);
    setShowPasswordFields({ password: false, confirm: false });
  };

  const closePasswordModal = () => {
    setPasswordModalUser(null);
    setPasswordForm({ password: "", confirmPassword: "" });
    setPasswordModalError(null);
  };

  const savePassword = async () => {
    if (!passwordModalUser) return;
    setPasswordModalError(null);
    if (!passwordForm.password) {
      setPasswordModalError("La nueva contraseña es obligatoria");
      return;
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      setPasswordModalError("Las contraseñas no coinciden");
      return;
    }
    const validationMsg = validatePlatformPassword(passwordForm.password);
    if (validationMsg) {
      setPasswordModalError(validationMsg);
      return;
    }
    setBusy(true);
    const res = await apiService.setPlatformUserPassword(passwordModalUser.id, {
      password: passwordForm.password,
      confirmPassword: passwordForm.confirmPassword,
    });
    setBusy(false);
    if (res.error) {
      setPasswordModalError(res.message || res.error);
      return;
    }
    closePasswordModal();
    setSuccessMessage(
      (res.data as { message?: string })?.message || "Contraseña actualizada correctamente"
    );
  };

  const passwordStrength = getPasswordStrength(passwordForm.password);

  const saveDates = async () => {
    if (!dateModalUser) return;
    setBusy(true);
    const res = await apiService.updatePlatformSubscription(dateModalUser.id, {
      accessStartsAt: dateForm.accessStartsAt || null,
      accessEndsAt: dateForm.accessEndsAt || null,
      status: "active",
    });
    setBusy(false);
    if (res.error) {
      setError(res.message || res.error);
      return;
    }
    setDateModalUser(null);
    load();
  };

  const handleReactivate = async (u: PlatformUserRow) => {
    setBusy(true);
    const res = await apiService.updatePlatformSubscription(u.id, { status: "active" });
    setBusy(false);
    if (res.error) setError(res.message || res.error);
    else load();
  };

  const handleDelete = async (u: PlatformUserRow) => {
    if (!window.confirm(`Eliminar usuario ${u.email}? Esta acción no se puede deshacer.`)) return;
    const res = await apiService.deletePlatformUser(u.id);
    if (res.error) setError(res.message || res.error);
    else load();
  };

  const inputCls = "w-full rounded-lg border border-[#E6E9F0] dark:border-[#30363d] px-3 py-2 text-sm bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] focus:outline-none focus:ring-2 focus:ring-[#635BFF]/20 focus:border-[#635BFF] transition-colors";

  const content = (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] p-6 shadow-stripe">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-[#635BFF] mb-1">
              <SparklesIcon className="h-3.5 w-3.5" />
              Administración FactuApp
            </div>
            <h2 className="text-xl font-semibold tracking-tight text-[#0A2540] dark:text-[#e6edf3]">
              Usuarios de la plataforma
            </h2>
            <p className="mt-1 text-sm text-[#425466] dark:text-[#8b949e] max-w-xl">
              Gestión de cuentas, suscripciones y actividad de cada tenant en el ERP.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowForm((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#635BFF] text-white px-4 py-2 text-sm font-semibold shadow-sm hover:bg-[#4F46E5] transition-colors"
            >
              <UserPlusIcon className="h-4 w-4" />
              Nuevo usuario
            </button>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#E6E9F0] dark:border-[#30363d] px-4 py-2 text-sm font-medium text-[#425466] dark:text-[#8b949e] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] disabled:opacity-50 transition-colors"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualizar
            </button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-[#E6E9F0] dark:border-[#30363d] p-2 text-[#8898aa] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: "Total usuarios", value: summary?.totalUsers ?? users.length, icon: UsersIcon },
            { label: "Activos", value: summary?.active ?? statusCounts.active, icon: CheckCircleIcon },
            { label: "Pausados", value: summary?.paused ?? statusCounts.paused, icon: PauseCircleIcon },
            {
              label: "Tokens IA (30 días)",
              value: formatTokens(aiTokensMonth),
              icon: SparklesIcon,
              hint: "Suma de todos los usuarios",
            },
          ].map(({ label, value, icon: Icon, hint }) => (
            <div
              key={label}
              className="rounded-xl border border-[#E6E9F0] dark:border-[#30363d] bg-[#F6F9FC] dark:bg-[#0d1117] px-4 py-3"
            >
              <div className="flex items-center gap-2 text-[#8898aa]">
                <Icon className="h-4 w-4 shrink-0" />
                <p className="text-xs font-medium">{label}</p>
              </div>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-[#0A2540] dark:text-[#e6edf3]">
                {value}
              </p>
              {hint && <p className="mt-0.5 text-[10px] text-[#8898aa]">{hint}</p>}
            </div>
          ))}
        </div>
      </div>

      {lastInvite && (
        <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/10 p-4 text-sm">
          <p className="font-semibold text-emerald-800 dark:text-emerald-300">Último enlace de invitación</p>
          <p className="mt-1 break-all text-emerald-700 dark:text-emerald-400 text-xs">{lastInvite.url}</p>
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 transition-colors"
            onClick={() => copyInvite(lastInvite.url)}
          >
            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
            Copiar enlace
          </button>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/10 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-300">
          <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-600" />
          <span>{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/10 px-4 py-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] p-5 shadow-stripe"
        >
          <p className="mb-3 text-sm font-semibold text-[#0A2540] dark:text-[#e6edf3]">Alta de nuevo usuario</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <input
              required
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
            />
            <input
              required
              type="text"
              placeholder="Nombre"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className={inputCls}
            />
            <input
              type="text"
              placeholder="Empresa (opcional)"
              value={form.companyName}
              onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#635BFF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4F46E5] disabled:opacity-50 transition-colors shadow-sm"
          >
            Crear y generar enlace
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-[#635BFF]/30 border-t-[#635BFF] rounded-full animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#E6E9F0] dark:border-[#30363d] py-12 text-center">
          <p className="text-sm text-[#8898aa]">No hay usuarios de plataforma todavía.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {users.map((u) => {
            const display = resolveDisplayStatus(u);
            const needsReactivate = display !== "active";
            const tokens30d = u.ai?.tokensTotal30d || 0;
            const aiPct = Math.min(100, Math.round((tokens30d / maxAiTokens30d) * 100));
            return (
              <article
                key={u.id}
                className="rounded-xl border border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] p-5 shadow-stripe hover:shadow-stripe-md transition-shadow"
              >
                <div className="flex flex-wrap items-start gap-4">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#635BFF] to-[#4F46E5] text-sm font-semibold text-white shadow-sm"
                    aria-hidden
                  >
                    {getInitials(u)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-[#0A2540] dark:text-[#e6edf3] text-sm truncate">
                          {u.email}
                        </p>
                        <p className="flex items-center gap-1 text-xs text-[#425466] dark:text-[#8b949e] mt-0.5">
                          <BuildingOffice2Icon className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{u.companyName || u.name}</span>
                        </p>
                      </div>
                      <span
                        className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${STATUS_STYLES[display]}`}
                      >
                        {STATUS_LABELS[display]}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div className="rounded-lg bg-[#F6F9FC] dark:bg-[#0d1117] border border-[#E6E9F0]/80 dark:border-[#30363d] px-2.5 py-2">
                        <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#8898aa]">
                          <DocumentTextIcon className="h-3 w-3" />
                          Facturas
                        </p>
                        <p className="text-sm font-semibold tabular-nums text-[#0A2540] dark:text-[#e6edf3]">
                          {u.stats?.invoiceCount ?? 0}
                        </p>
                      </div>
                      <div className="rounded-lg bg-[#F6F9FC] dark:bg-[#0d1117] border border-[#E6E9F0]/80 dark:border-[#30363d] px-2.5 py-2">
                        <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#8898aa]">
                          <BanknotesIcon className="h-3 w-3" />
                          Gastos
                        </p>
                        <p className="text-sm font-semibold tabular-nums text-[#0A2540] dark:text-[#e6edf3]">
                          {u.stats?.expenseCount ?? 0}
                        </p>
                      </div>
                      <div className="rounded-lg bg-[#F6F9FC] dark:bg-[#0d1117] border border-[#E6E9F0]/80 dark:border-[#30363d] px-2.5 py-2 col-span-2 sm:col-span-2">
                        <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#8898aa]">
                          <ClockIcon className="h-3 w-3" />
                          Último acceso
                        </p>
                        <p className="text-xs font-medium text-[#0A2540] dark:text-[#e6edf3] truncate">
                          {formatDateTimeDisplay(u.stats?.lastLoginAt)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="flex items-center gap-1 text-[#8898aa]">
                          <SparklesIcon className="h-3.5 w-3.5 text-[#635BFF]" />
                          Tokens IA (30 días)
                        </span>
                        <span className="font-semibold tabular-nums text-[#635BFF]">
                          {formatTokens(tokens30d)}
                          {u.ai?.requestsCount ? (
                            <span className="font-normal text-[#8898aa] ml-1">
                              · {u.ai.requestsCount} solicitudes
                            </span>
                          ) : null}
                        </span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-[#E6E9F0] dark:bg-[#30363d]">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-[#635BFF] to-[#4F46E5] transition-all"
                          style={{ width: `${tokens30d ? aiPct : 0}%` }}
                        />
                      </div>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-[#8898aa]">
                      <span>Acceso: {formatDateDisplay(u.accessStartsAt)} → {formatDateDisplay(u.accessEndsAt)}</span>
                      <span>
                        {u.onboardingCompleted ? "Cuenta activada" : "Invitación pendiente"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-1.5 border-t border-[#E6E9F0] dark:border-[#21262d] pt-4">
                  <button
                    type="button"
                    title="Regenerar invitación"
                    onClick={() => handleRegenerate(u.id)}
                    disabled={busy}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#E6E9F0] dark:border-[#30363d] px-2.5 py-1 text-xs font-medium text-[#425466] dark:text-[#8b949e] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] disabled:opacity-50 transition-colors"
                  >
                    <ArrowPathIcon className="h-3.5 w-3.5" />
                    Invitación
                  </button>
                  <button
                    type="button"
                    onClick={() => openDateModal(u)}
                    disabled={busy}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#E6E9F0] dark:border-[#30363d] px-2.5 py-1 text-xs font-medium text-[#425466] dark:text-[#8b949e] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] disabled:opacity-50 transition-colors"
                  >
                    <CalendarDaysIcon className="h-3.5 w-3.5" />
                    Fechas
                  </button>
                  <button
                    type="button"
                    onClick={() => openPasswordModal(u)}
                    disabled={busy}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#635BFF]/30 bg-[#635BFF]/5 px-2.5 py-1 text-xs font-medium text-[#635BFF] hover:bg-[#635BFF]/10 disabled:opacity-50 transition-colors"
                  >
                    <KeyIcon className="h-3.5 w-3.5" />
                    Contraseña
                  </button>
                  {display !== "paused" && display !== "cancelled" && (
                    <button
                      type="button"
                      onClick={() => handlePause(u)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800/40 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-100 transition-colors"
                    >
                      <PauseCircleIcon className="h-3.5 w-3.5" />
                      Pausar
                    </button>
                  )}
                  {display !== "cancelled" && (
                    <button
                      type="button"
                      onClick={() => handleCancel(u)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800/40 px-2.5 py-1 text-xs font-medium text-red-700 dark:text-red-400 hover:bg-red-100 transition-colors"
                    >
                      <NoSymbolIcon className="h-3.5 w-3.5" />
                      Cancelar
                    </button>
                  )}
                  {needsReactivate && (
                    <button
                      type="button"
                      onClick={() => handleReactivate(u)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 transition-colors"
                    >
                      Reactivar
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(u)}
                    disabled={busy}
                    className="ml-auto rounded-lg p-1.5 text-[#8898aa] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Eliminar"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {passwordModalUser && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] p-6 shadow-stripe-lg">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3]">Cambiar contraseña</h3>
                <p className="text-xs text-[#8898aa]">{passwordModalUser.email}</p>
              </div>
              <button type="button" onClick={closePasswordModal} className="rounded-lg p-1.5 text-[#8898aa] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] hover:text-[#425466] transition-colors">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-xs text-[#8898aa]">
              Mínimo 10 caracteres con mayúscula, minúscula, número y símbolo.
            </p>
            {passwordModalError && (
              <div className="mt-3 rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/10 px-3 py-2 text-sm text-red-700 dark:text-red-400">
                {passwordModalError}
              </div>
            )}
            <div className="mt-4 space-y-3">
              <label className="block text-sm">
                <span className="text-sm font-medium text-[#0A2540] dark:text-[#e6edf3]">Nueva contraseña</span>
                <div className="relative mt-1.5">
                  <input
                    type={showPasswordFields.password ? "text" : "password"}
                    autoComplete="new-password"
                    value={passwordForm.password}
                    onChange={(e) => {
                      setPasswordForm((f) => ({ ...f, password: e.target.value }));
                      setPasswordModalError(null);
                    }}
                    className={`${inputCls} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswordFields((s) => ({ ...s, password: !s.password }))
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[#8898aa] hover:text-[#425466]"
                    tabIndex={-1}
                  >
                    {showPasswordFields.password ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </label>
              {passwordForm.password.length > 0 && (
                <div className="rounded-lg bg-[#F6F9FC] dark:bg-[#0d1117] border border-[#E6E9F0] dark:border-[#30363d] px-3 py-2.5 text-xs">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#8898aa]">Fortaleza</span>
                    <span className={`font-medium ${passwordStrength.color}`}>
                      {passwordStrength.strength}
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-[#E6E9F0] dark:bg-[#30363d]">
                    <div
                      className={`h-full rounded-full transition-all ${
                        passwordStrength.score <= 2
                          ? "bg-red-500"
                          : passwordStrength.score <= 3
                            ? "bg-amber-500"
                            : "bg-emerald-500"
                      }`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    />
                  </div>
                  <ul className="mt-2 grid grid-cols-2 gap-1 text-[#8898aa]">
                    {(
                      [
                        ["length", "10+ caracteres"],
                        ["lowercase", "Minúscula"],
                        ["uppercase", "Mayúscula"],
                        ["number", "Número"],
                        ["special", "Símbolo"],
                      ] as const
                    ).map(([key, label]) => (
                      <li
                        key={key}
                        className={
                          passwordStrength.checks[key]
                            ? "text-emerald-600 dark:text-emerald-400"
                            : ""
                        }
                      >
                        {passwordStrength.checks[key] ? "✓ " : "○ "}
                        {label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <label className="block text-sm">
                <span className="text-sm font-medium text-[#0A2540] dark:text-[#e6edf3]">Confirmar contraseña</span>
                <div className="relative mt-1.5">
                  <input
                    type={showPasswordFields.confirm ? "text" : "password"}
                    autoComplete="new-password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => {
                      setPasswordForm((f) => ({ ...f, confirmPassword: e.target.value }));
                      setPasswordModalError(null);
                    }}
                    className={`${inputCls} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswordFields((s) => ({ ...s, confirm: !s.confirm }))
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[#8898aa] hover:text-[#425466]"
                    tabIndex={-1}
                  >
                    {showPasswordFields.confirm ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closePasswordModal}
                className="px-4 py-2 text-sm font-medium text-[#425466] dark:text-[#8b949e] border border-[#E6E9F0] dark:border-[#30363d] rounded-lg hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={savePassword}
                disabled={busy}
                className="px-4 py-2 text-sm font-semibold text-white bg-[#635BFF] rounded-lg hover:bg-[#4F46E5] disabled:opacity-50 transition-colors shadow-sm"
              >
                Guardar contraseña
              </button>
            </div>
          </div>
        </div>
      )}

      {dateModalUser && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] p-6 shadow-stripe-lg">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3]">Ventana de acceso</h3>
                <p className="text-xs text-[#8898aa]">{dateModalUser.email}</p>
              </div>
              <button type="button" onClick={() => setDateModalUser(null)} className="rounded-lg p-1.5 text-[#8898aa] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] transition-colors">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-xs text-[#8898aa]">
              Fuera del rango, con suscripción pausada o cancelada, el usuario verá «Contacta con el administrador» al iniciar sesión.
            </p>
            <div className="mt-4 grid gap-3">
              <label className="text-sm">
                <span className="text-sm font-medium text-[#0A2540] dark:text-[#e6edf3]">Inicio (opcional)</span>
                <input
                  type="date"
                  value={dateForm.accessStartsAt}
                  onChange={(e) => setDateForm((f) => ({ ...f, accessStartsAt: e.target.value }))}
                  className={`${inputCls} mt-1.5`}
                />
              </label>
              <label className="text-sm">
                <span className="text-sm font-medium text-[#0A2540] dark:text-[#e6edf3]">Fin (opcional)</span>
                <input
                  type="date"
                  value={dateForm.accessEndsAt}
                  onChange={(e) => setDateForm((f) => ({ ...f, accessEndsAt: e.target.value }))}
                  className={`${inputCls} mt-1.5`}
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDateModalUser(null)}
                className="px-4 py-2 text-sm font-medium text-[#425466] dark:text-[#8b949e] border border-[#E6E9F0] dark:border-[#30363d] rounded-lg hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] transition-colors"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={saveDates}
                disabled={busy}
                className="px-4 py-2 text-sm font-semibold text-white bg-[#635BFF] rounded-lg hover:bg-[#4F46E5] disabled:opacity-50 transition-colors shadow-sm"
              >
                Guardar fechas
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (variant === "modal") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
        <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-[#E6E9F0] dark:border-[#21262d] bg-[#F6F9FC] dark:bg-[#0d1117] p-6 shadow-stripe-lg">
          {content}
        </div>
      </div>
    );
  }
  return <div className="max-w-4xl mx-auto">{content}</div>;
};

export default PlatformUsersPanel;
